/**
 * Which screen the assistant was opened from (agent-1-web W2, WC1): the
 * `Context` the drawer sends with every message and the words of its chip.
 *
 * The backend's route enum is `home, report, state, record, check-in, baseline,
 * history, recording, account, assistant` (agent-1 C18). Sub-routes map to their
 * parent -- `/record/connect` is Record, `/account/data` is Account -- and the
 * one detail route, `/history/:recordingId`, is `recording` with its id, so the
 * server reads THAT recording rather than the newest one.
 *
 * Anything else -- `/admin/*`, `/_specimen`, an unknown address -- is not a
 * product screen, and the bubble does not appear there (criterion 3).
 */
import type { ChatCard, ChatContext } from './assistant'
import { ROUTE_TITLE } from './assistantCopy'
import { SCORE_SLOTS } from './dailyStatus'

/** The client screens emitted by screenFor, including paired and experiment routes.
 * The generated API ChatRoute stays broader for wire and stored-message labels.
 */
export type ScreenRouteName =
  | 'home'
  | 'report'
  | 'state'
  | 'record'
  | 'check-in'
  | 'baseline'
  | 'history'
  | 'recording'
  | 'compare'
  | 'experiment'
  | 'account'
  | 'assistant'

export interface ScreenRoute {
  route: ScreenRouteName
  recordingId: string | null
  compareWith?: string
  experimentRunId?: string
}

const TOP: Readonly<Record<string, ScreenRouteName>> = {
  home: 'home',
  report: 'report',
  today: 'report',
  state: 'state',
  record: 'record',
  'check-in': 'check-in',
  baseline: 'baseline',
  history: 'history',
  account: 'account',
  assistant: 'assistant',
}

/** The screen a pathname is, or null when it is not a product screen. */
export function screenFor(pathname: string, experimentsEnabled = false): ScreenRoute | null {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] === 'history' && parts[1] === 'compare') {
    if (!experimentsEnabled || parts.length !== 4) return null
    const before = decodeId(parts[2])
    const after = decodeId(parts[3])
    return before && after ? { route: 'compare', recordingId: before, compareWith: after } : null
  }
  if (parts[0] === 'experiment-runs') {
    if (!experimentsEnabled || parts.length !== 2) return null
    const run = decodeId(parts[1])
    return run && UUID.test(run) ? { route: 'experiment', recordingId: null, experimentRunId: run } : null
  }
  const [first = '', second, ...rest] = pathname.split('/').filter(Boolean)
  const route = Object.hasOwn(TOP, first) ? TOP[first] : undefined
  if (!route) return null
  if (route === 'history' && second !== undefined) {
    // Only `/history/:recordingId` itself; nothing deeper exists.
    if (rest.length !== 0) return null
    let recordingId: string
    try {
      recordingId = decodeURIComponent(second)
    } catch {
      return null
    }
    return { route: 'recording', recordingId }
  }
  return { route, recordingId: null }
}

/** What a page tells the drawer about itself, beyond its address. */
export interface PageContext {
  /** The reading the page shows, when it shows one. */
  recordingId?: string | null
  /** EC / EO, when the page is about one condition. */
  condition?: 'EC' | 'EO' | null
  /** The page's own date words for that reading ("11 Oct"), shown in the chip. */
  dateLabel?: string | null
  /** Pair/run words rendered by the source page, taken from its payload. */
  pairLabel?: string | null
  activityLabel?: string | null
}


/** A measure the person asked about from its card ("Ask about this"). */
export interface MeasureFocus {
  ref: string
  /** The card's own heading, verbatim. */
  title: string
}

const RECORDING_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,254}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const MEASURE_REF = /^[a-z][a-z0-9_]{0,63}$/

function decodeId(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value)
    return RECORDING_ID.test(decoded) ? decoded : null
  } catch {
    return null
  }
}

/**
 * The `Context` for a message, from the screen, what the page published, and
 * the measure in focus. Ids the contract would refuse are dropped rather than
 * sent: a 400 is the server's answer to a malformed id, and the person did
 * nothing wrong.
 */
export function chatContext(screen: ScreenRoute, page: PageContext | null, measure: MeasureFocus | null): ChatContext {
  const recording = screen.recordingId ?? page?.recordingId ?? null
  return {
    route: screen.route,
    recording_id: recording !== null && RECORDING_ID.test(recording) ? recording : null,
    measure_ref: measure && MEASURE_REF.test(measure.ref) ? measure.ref : null,
    condition: page?.condition ?? null,
    ...(screen.route === 'compare' ? { compare_with: screen.compareWith ?? null } : {}),
    ...(screen.route === 'experiment' ? { experiment_run_id: screen.experimentRunId ?? null } : {}),
  }
}

const CONDITION_WORDS = { EC: 'eyes closed', EO: 'eyes open' } as const

/** "Viewing · Report · 11 Oct · eyes closed · Mental activation" */
export function chipLabel(screen: ScreenRoute, page: PageContext | null, measure: MeasureFocus | null): string {
  if (screen.route === 'compare' || screen.route === 'experiment') {
    return ['Viewing', page?.activityLabel ?? ROUTE_TITLE[screen.route], page?.dateLabel,
      page?.pairLabel ?? 'before and after', measure?.title].filter(Boolean).join(' · ')
  }
  const parts = [
    'Viewing',
    ROUTE_TITLE[screen.route],
    page?.dateLabel ?? null,
    page?.condition ? CONDITION_WORDS[page.condition] : null,
    measure?.title ?? null,
  ]
  return parts.filter((p): p is string => Boolean(p)).join(' · ')
}

/** A card links only to its stored source, independent of the current route. */
export function cardTarget(card: ChatCard): { href: string; label: string } | null {
  const recording = card.recording_id
  if (!recording) return null
  const anchor = (SCORE_SLOTS as readonly string[]).includes(card.ref) ? `score-${card.ref}` : `score-ring-${card.ref}`
  return { href: `/history/${encodeURIComponent(recording)}#${anchor}`, label: 'Open the full card in History ›' }
}
